import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { SceneCanvasComponent } from "./scene/scene-canvas.component";
import { SettingsDrawerComponent } from "./shell/settings-drawer.component";
import { SplashScreenComponent } from "./shell/splash-screen.component";
import {
  GameSetupScreenComponent,
  type GameSetupLaunch,
} from "./shell/game-setup-screen.component";
import { ControlsHostComponent } from "./controls/controls-host.component";
import { InstrumentHudComponent } from "./hud/instrument-hud.component";
import { CrewPanelComponent } from "./crew/crew-panel.component";
import { AnchorContextMenuComponent } from "./crew/anchor-context-menu.component";
import {
  PracticeSetupComponent,
  type PracticeLaunch,
} from "./practice/practice-setup.component";
import { PracticeHistoryComponent } from "./practice/practice-history.component";
import { ManeuverHudComponent } from "./practice/maneuver-hud.component";
import { PracticeResultComponent } from "./practice/practice-result.component";
import {
  PracticeRunnerService,
  PracticeStartError,
  type PracticeStartErrorCode,
} from "./practice/practice-runner.service";
import { GamificationConfigService } from "./core/gamification/config.service";
import { SimService } from "./core/sim/sim.service";
import { tierEnvironment } from "./core/gamification/tier-environment";
import { APP_CONFIG } from "./core/app-config";
import {
  CASTOFFPRO_PENDING_NEW_GAME_STATE,
  CASTOFFPRO_PLAYER_BOAT_ID,
  CASTOFFPRO_SETUP_PREVIEW,
  CASTOFFPRO_TERRAIN_ID,
} from "./core/storage/storage-keys";
// Deep imports (NOT the barrel) so loading the root shell does not
// evaluate `simulation-core/main.ts` and pull the entire renderer /
// CastOffProApp graph into the initial bundle. CastOffProApp is loaded lazily
// inside `SimService.attach()` via `await import("../simulation-core")`.
import { defaultAppState } from "../simulation-core/State";
import { DEFAULT_SCENE, applySceneToState } from "../simulation-core/sim/Scene";
import type { AppState } from "../simulation-core/State";

/**
 * Root shell. Layout:
 *   - WebGPU canvas fills the viewport.
 *   - Settings drawer (gear icon, top-right) — slides in on demand;
 *     also houses the audio mute / volume controls.
 *   - Controls host (helm + engine widgets) — lazy via @defer once the
 *     simulation handle is ready.
 *
 * No NgModule, no routes (yet). Standalone components imported directly.
 * @defer keeps the controls bundle out of the initial chunk.
 */
@Component({
  selector: "app-root",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SceneCanvasComponent,
    SettingsDrawerComponent,
    SplashScreenComponent,
    GameSetupScreenComponent,
    ControlsHostComponent,
    InstrumentHudComponent,
    CrewPanelComponent,
    AnchorContextMenuComponent,
    PracticeSetupComponent,
    PracticeHistoryComponent,
    ManeuverHudComponent,
    PracticeResultComponent,
  ],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.css",
})
export class AppComponent {
  private readonly config = inject(APP_CONFIG);
  private readonly pendingNewGameState = consumePendingNewGameState();
  private readonly requestedSetupPreview = consumeSetupPreviewRequest();

  readonly canvasReady = signal(false);
  readonly backgroundPreview = signal(
    this.pendingNewGameState === null &&
      (this.config.showStartScreen || this.requestedSetupPreview),
  );
  readonly setupInitialState = signal<Partial<AppState> | null>(
    this.pendingNewGameState ??
      (this.backgroundPreview() ? createSetupPreviewState() : null),
  );
  readonly setupOpen = signal(
    this.backgroundPreview() && this.pendingNewGameState === null,
  );
  readonly initialSetupRequired = signal(
    this.config.showStartScreen && !this.requestedSetupPreview,
  );
  readonly setupClosable = computed(() => !this.initialSetupRequired());

  readonly practiceOpen = signal(false);

  private readonly gamification = inject(GamificationConfigService);
  private readonly sim = inject(SimService);
  private readonly practiceRunner = inject(PracticeRunnerService);

  /** True while a Practice maneuver is in flight (drives HUD visibility). */
  readonly practiceRunning = this.practiceRunner.isRunning;
  /** Non-null while the result modal should be shown. */
  readonly practiceResult = this.practiceRunner.lastResult;
  /** Last launch payload, so Retry can re-issue it without reopening setup. */
  private lastLaunch: PracticeLaunch | null = null;

  openPractice(): void {
    this.practiceOpen.set(true);
  }

  closePractice(): void {
    this.practiceOpen.set(false);
  }

  startPractice(launch: PracticeLaunch): void {
    // Apply the tier's mid-range wind / wave to the sim so the player
    // immediately feels the difficulty change. Boat / terrain selection
    // requires a scene reload (same constraint as the regular new-game
    // flow); for this MVP iteration we only override the environment.
    const scoring = this.gamification.scoring();
    if (scoring) {
      const env = tierEnvironment(scoring, launch.tier);
      this.sim.overrideEnvironment(env);
    }
    this.lastLaunch = launch;
    this.practiceOpen.set(false);
    void this.practiceRunner.start(launch).catch((err: unknown) => {
      console.error("[practice] failed to start", err);
      if (err instanceof PracticeStartError) {
        // Reopen the setup so the user can retry / adjust.
        this.practiceOpen.set(true);
        const msg = practiceStartErrorMessage(err.code);
        // Plain alert is fine for the MVP smoke surface; a proper
        // toast component lands in Etap 2.
        if (typeof window !== "undefined") window.alert(msg);
      }
    });
  }

  retryPractice(): void {
    if (this.lastLaunch) {
      this.startPractice(this.lastLaunch);
    }
  }

  reopenPracticeSetup(): void {
    this.practiceOpen.set(true);
  }

  startFromSetup(setup: GameSetupLaunch): void {
    persistNewGameSetup(setup.initialState);
    window.location.reload();
  }

  openNewGameSetup(): void {
    requestSetupPreview();
    window.location.reload();
  }

  closeNewGameSetup(): void {
    if (this.initialSetupRequired()) return;
    window.location.reload();
  }
}

const PENDING_NEW_GAME_STATE_KEY = CASTOFFPRO_PENDING_NEW_GAME_STATE;
const SETUP_PREVIEW_KEY = CASTOFFPRO_SETUP_PREVIEW;

function practiceStartErrorMessage(code: PracticeStartErrorCode): string {
  switch (code) {
    case "anchor_not_deployed":
      return "Najpierw rzuć kotwicę — manewr 'Podnoszenie kotwicy' wymaga rozłożonej kotwicy.";
    case "sim_not_ready":
      return "Symulacja jeszcze nie jest gotowa. Spróbuj za chwilę.";
    case "unknown_maneuver":
      return "Nieznany manewr. Wybierz inny z listy.";
  }
}

function createSetupPreviewState(): AppState {
  const state = defaultAppState();
  applySceneToState(DEFAULT_SCENE, state);
  state.showWindStreaks = false;
  state.showBirds = false;
  return state;
}

function consumePendingNewGameState(): Partial<AppState> | null {
  try {
    const raw = sessionStorage.getItem(PENDING_NEW_GAME_STATE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_NEW_GAME_STATE_KEY);
    return JSON.parse(raw) as Partial<AppState>;
  } catch {
    return null;
  }
}

function persistNewGameSetup(state: Partial<AppState>): void {
  try {
    sessionStorage.removeItem(SETUP_PREVIEW_KEY);
    sessionStorage.setItem(PENDING_NEW_GAME_STATE_KEY, JSON.stringify(state));
    if (state.terrainId)
      localStorage.setItem(CASTOFFPRO_TERRAIN_ID, state.terrainId);
    if (state.playerBoatId) {
      localStorage.setItem(CASTOFFPRO_PLAYER_BOAT_ID, state.playerBoatId);
    }
  } catch {
    // If storage is unavailable, fall through to a plain reload with existing defaults.
  }
}

function consumeSetupPreviewRequest(): boolean {
  try {
    const requested = sessionStorage.getItem(SETUP_PREVIEW_KEY) === "1";
    sessionStorage.removeItem(SETUP_PREVIEW_KEY);
    return requested;
  } catch {
    return false;
  }
}

function requestSetupPreview(): void {
  try {
    sessionStorage.setItem(SETUP_PREVIEW_KEY, "1");
  } catch {
    // Ignore storage failures; the reload simply returns to the current game.
  }
}
